/**
 * Groups Store - Manages pods/groups state
 *
 * Handles group selection, membership, and data fetching.
 */

import { signal, computed, effect } from '@preact/signals';
import type { Group, GroupMember } from '@/types';
import { api } from '@/api/client';
import { isAuthenticated } from './auth';

// ============================================
// STATE
// ============================================

export const groups = signal<Group[]>([]);
export const selectedGroupId = signal<string | null>(null);
export const members = signal<GroupMember[]>([]);
export const isLoading = signal(false);
export const error = signal<string | null>(null);

// ============================================
// COMPUTED
// ============================================

export const selectedGroup = computed(() => {
  if (!selectedGroupId.value) return null;
  return groups.value.find(g => g.id === selectedGroupId.value) || null;
});

export const hasGroups = computed(() => groups.value.length > 0);

export const currentPhase = computed(() => {
  return selectedGroup.value?.periods.currentPhase || 'between';
});

export const canSubmit = computed(() => {
  const phase = currentPhase.value;
  return phase === 'submission';
});

export const canVote = computed(() => {
  const phase = currentPhase.value;
  return phase === 'voting';
});

// ============================================
// PERSISTENCE
// ============================================

const SELECTED_GROUP_KEY = 'sherit_selected_group';

function loadSelectedGroup() {
  const saved = localStorage.getItem(SELECTED_GROUP_KEY);
  if (saved) {
    selectedGroupId.value = saved;
  }
}

// Auto-save selected group
effect(() => {
  const id = selectedGroupId.value;
  if (id) {
    localStorage.setItem(SELECTED_GROUP_KEY, id);
  } else {
    localStorage.removeItem(SELECTED_GROUP_KEY);
  }
});

// ============================================
// ACTIONS
// ============================================

/**
 * Fetch user's groups
 */
export async function fetchGroups(): Promise<void> {
  if (!isAuthenticated.value) {
    groups.value = [];
    return;
  }

  isLoading.value = true;
  error.value = null;

  try {
    const response = await api.get<{ groups: Group[] }>('/api/groups');

    if (response.data?.groups) {
      groups.value = response.data.groups;

      // Load saved selection or select first group
      loadSelectedGroup();
      if (!selectedGroupId.value && groups.value.length > 0) {
        selectedGroupId.value = groups.value[0].id;
      }

      // Validate selection still exists
      if (selectedGroupId.value && !groups.value.find(g => g.id === selectedGroupId.value)) {
        selectedGroupId.value = groups.value[0]?.id || null;
      }
    }
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to fetch groups';
    console.error('Failed to fetch groups:', e);
  } finally {
    isLoading.value = false;
  }
}

/**
 * Select a group
 */
export function selectGroup(groupId: string): void {
  if (groups.value.find(g => g.id === groupId)) {
    selectedGroupId.value = groupId;
  }
}

/**
 * Fetch group members
 */
export async function fetchMembers(groupId: string): Promise<void> {
  if (!isAuthenticated.value) return;

  try {
    const response = await api.get<{ members: GroupMember[] }>(`/api/groups/${groupId}/members`);
    if (response.data?.members) {
      members.value = response.data.members;
    }
  } catch (e) {
    console.error('Failed to fetch members:', e);
  }
}

/**
 * Create a new group
 */
export async function createGroup(data: {
  name: string;
  description?: string;
  cycleFrequency?: 'monthly' | 'biweekly' | 'quarterly';
}): Promise<Group | null> {
  if (!isAuthenticated.value) return null;

  isLoading.value = true;
  error.value = null;

  try {
    const response = await api.post<{ group: Group }>('/api/groups', data);

    if (response.data?.group) {
      groups.value = [...groups.value, response.data.group];
      selectedGroupId.value = response.data.group.id;
      return response.data.group;
    }

    error.value = response.error || 'Failed to create group';
    return null;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to create group';
    return null;
  } finally {
    isLoading.value = false;
  }
}

/**
 * Join a group with invite code
 */
export async function joinGroup(inviteCode: string): Promise<Group | null> {
  if (!isAuthenticated.value) return null;

  isLoading.value = true;
  error.value = null;

  try {
    const response = await api.post<{ group: Group }>(`/api/groups/join/${inviteCode}`);

    if (response.data?.group) {
      groups.value = [...groups.value, response.data.group];
      selectedGroupId.value = response.data.group.id;
      return response.data.group;
    }

    error.value = response.error || 'Invalid invite code';
    return null;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Failed to join group';
    return null;
  } finally {
    isLoading.value = false;
  }
}

/**
 * Leave a group
 */
export async function leaveGroup(groupId: string): Promise<boolean> {
  if (!isAuthenticated.value) return false;

  try {
    const response = await api.post(`/api/groups/${groupId}/leave`);

    if (!response.error) {
      groups.value = groups.value.filter(g => g.id !== groupId);

      // Select another group if we left the selected one
      if (selectedGroupId.value === groupId) {
        selectedGroupId.value = groups.value[0]?.id || null;
      }

      return true;
    }

    return false;
  } catch (e) {
    console.error('Failed to leave group:', e);
    return false;
  }
}

/**
 * Update group settings (admin only)
 */
export async function updateGroup(
  groupId: string,
  updates: Partial<Pick<Group, 'name' | 'description' | 'cycleFrequency'>>
): Promise<boolean> {
  if (!isAuthenticated.value) return false;

  try {
    const response = await api.put<{ group: Group }>(`/api/groups/${groupId}`, updates);

    if (response.data?.group) {
      groups.value = groups.value.map(g =>
        g.id === groupId ? response.data!.group : g
      );
      return true;
    }

    return false;
  } catch (e) {
    console.error('Failed to update group:', e);
    return false;
  }
}

/**
 * Refresh selected group data
 */
export async function refreshSelectedGroup(): Promise<void> {
  if (!selectedGroupId.value || !isAuthenticated.value) return;

  try {
    const response = await api.get<{ group: Group }>(`/api/groups/${selectedGroupId.value}`);

    if (response.data?.group) {
      groups.value = groups.value.map(g =>
        g.id === selectedGroupId.value ? response.data!.group : g
      );
    }
  } catch (e) {
    console.error('Failed to refresh group:', e);
  }
}
